import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, X, Settings } from 'lucide-react';

export function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({
    essential: true,
    analytics: true,
    preferences: true,
    marketing: true
  });

  useEffect(() => {
    // Check if user has already consented
    const storedConsent = localStorage.getItem('cookie_consent');
    if (!storedConsent) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const fullConsent = {
      essential: true,
      analytics: true,
      preferences: true,
      marketing: true,
      consented_at: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(fullConsent));
    window.__applyCookieConsent?.(fullConsent);
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    const minimalConsent = {
      essential: true,
      analytics: false,
      preferences: false,
      marketing: false,
      consented_at: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(minimalConsent));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const customConsent = {
      ...consent,
      consented_at: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(customConsent));
    window.__applyCookieConsent?.(customConsent);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 bg-background/95 backdrop-blur-lg border-t shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="max-w-6xl mx-auto">
        {!showDetails ? (
          // Simple banner
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Cookie className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">We use cookies</p>
                <p className="text-sm text-muted-foreground">
                  We use cookies to enhance your browsing experience and analyze site traffic.{' '}
                  <a href="/privacy" className="text-primary underline">Read our Privacy Policy</a>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetails(true)}
                className="gap-1"
              >
                <Settings className="w-4 h-4" />
                Customize
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectNonEssential}
              >
                Reject All
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
              >
                Accept All
              </Button>
            </div>
          </div>
        ) : (
          // Detailed preferences
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold">Cookie Preferences</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDetails(false)}
                aria-label="Close cookie preferences"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Essential</p>
                  <p className="text-xs text-muted-foreground">Required for site function</p>
                </div>
                <div className="text-sm text-muted-foreground">Always on</div>
              </div>

              <label className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/70 transition-colors">
                <div>
                  <p className="font-medium text-sm">Analytics</p>
                  <p className="text-xs text-muted-foreground">Help us improve</p>
                </div>
                <input
                  type="checkbox"
                  checked={consent.analytics}
                  onChange={(e) => setConsent(prev => ({ ...prev, analytics: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300"
                />
              </label>

              <label className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/70 transition-colors">
                <div>
                  <p className="font-medium text-sm">Preferences</p>
                  <p className="text-xs text-muted-foreground">Remember settings</p>
                </div>
                <input
                  type="checkbox"
                  checked={consent.preferences}
                  onChange={(e) => setConsent(prev => ({ ...prev, preferences: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300"
                />
              </label>

              <label className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/70 transition-colors">
                <div>
                  <p className="font-medium text-sm">Marketing</p>
                  <p className="text-xs text-muted-foreground">Personalized ads</p>
                </div>
                <input
                  type="checkbox"
                  checked={consent.marketing}
                  onChange={(e) => setConsent(prev => ({ ...prev, marketing: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectNonEssential}
              >
                Reject All
              </Button>
              <Button
                size="sm"
                onClick={handleSavePreferences}
              >
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
